import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useLocation } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, Wrench, Calendar, Tag } from 'lucide-react';
import PageTransition from '../../components/common/PageTransition';
import SEOHead from '../../components/common/SEOHead';
import { getProjectById } from '../../data/projects';

const ProjectDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const project = id ? getProjectById(id) : undefined;
  
  const fromProjectsList = location.state?.from === 'projects';
  const backLink = fromProjectsList ? '/art/projects' : '/art#projects';
  const backText = fromProjectsList ? t('art.projects.backToProjects') : t('art.projects.backToArt');

  if (!project) {
    return (
      <PageTransition>
        <SEOHead title={t('art.projects.noProject')} />
        <div className="flex flex-col items-center justify-center min-h-[50vh] space-y-6">
          <div className="w-20 h-20 rounded-2xl bg-brand-amber/10 border border-brand-amber/20 flex items-center justify-center">
            <Tag className="w-8 h-8 text-brand-amber" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            {t('art.projects.noProject')}
          </h1>
          <Link
            to={backLink}
            className="inline-flex items-center gap-2 text-brand-amber font-bold hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            {backText}
          </Link>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <SEOHead title={project.title} />
      <div className="space-y-8 max-w-4xl mx-auto">
        {/* Back navigation */}
        <Link
          to={backLink}
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-brand-amber transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {backText}
        </Link>

        {/* Hero Image */}
        <div className="relative overflow-hidden rounded-2xl art-card">
          <img
            src={project.imageUrl}
            alt={project.title}
            className="w-full h-64 sm:h-80 md:h-96 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-brand-amber" />
              <span className="text-sm font-semibold text-white/80">{project.date}</span>
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white font-heading">
              {t(`projects.${project.id.replace('project-', '')}.title`, { defaultValue: project.title })}
            </h1>
          </div>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-6">
            <div className="art-card rounded-2xl p-6 space-y-4">
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">
                {t(project.longDescription || project.description, { defaultValue: project.longDescription || project.description })}
              </p>

              {/* Tags */}
              <div className="flex flex-wrap gap-2">
                {project.tags.map(tag => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 bg-brand-amber/10 border border-brand-amber/20 rounded-lg text-xs font-bold text-brand-amber uppercase"
                  >
                    {t(`tags.${tag}`, { defaultValue: tag })}
                  </span>
                ))}
              </div>
            </div>

            {/* External Link */}
            {project.link && (
              <a
                href={project.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-8 py-4 rounded-xl bg-brand-amber hover:bg-amber-400 text-slate-900 font-bold text-lg transition-all shadow-lg shadow-brand-amber/20 hover:shadow-brand-amber/30 hover:scale-[1.02] active:scale-[0.98]"
              >
                {t('art.projects.visitProject')}
                <ArrowUpRight className="w-5 h-5" />
              </a>
            )}

            {/* Gallery */}
            {project.images && project.images.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-6 animate-fade-in">
                {project.images.map((img, idx) => (
                  <div key={idx} className="rounded-xl overflow-hidden art-card border-white/5">
                    <img 
                      src={img} 
                      alt={`${project.title} - ${idx + 1}`} 
                      className="w-full h-48 sm:h-64 object-cover hover:scale-105 transition-transform duration-500" 
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Tools */}
            <div className="art-card rounded-2xl p-5 space-y-3">
              <div className="flex items-center gap-2">
                <Wrench className="w-4 h-4 text-brand-amber" />
                <h3 className="font-bold text-slate-900 dark:text-white text-sm">
                  {t('art.projects.tools')}
                </h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {project.tools.map(tool => (
                  <span
                    key={tool}
                    className="px-2.5 py-1 bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 rounded-lg text-xs font-medium text-slate-600 dark:text-slate-300"
                  >
                    {tool}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageTransition>
  );
};

export default ProjectDetail;
